import { React, useMemo, useState } from 'react';

import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FindInPageRoundedIcon from '@mui/icons-material/FindInPageRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid } from '@mui/x-data-grid';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';
import { parseNumberPtBr } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import '../../components/agreementWorkflowPage.css';

const isValidValor = (value) => value !== '' && !Number.isNaN(Number(value)) && Number(value) >= 0;

const RecebimentoLimpaNome = () => {
    const [documento, setDocumento] = useState('');
    const [rows, setRows] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [valorRecebido, setValorRecebido] = useState('');
    const [dataRecebimento, setDataRecebimento] = useState(null);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    const selectedRow = rows.find((row) => row.id === selectedId) || null;
    const valorValido = isValidValor(valorRecebido);
    const canSubmit = Boolean(selectedRow) && valorValido && Boolean(dataRecebimento);
    const completedFields = [documento, selectedRow, valorValido, dataRecebimento].filter(Boolean).length;

    const billColumns = useMemo(() => [
        {
            field: 'selecionar',
            headerName: '',
            width: 60,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Radio
                    size='small'
                    checked={params.id === selectedId}
                    onChange={() => setSelectedId(params.id)}
                    inputProps={{ 'aria-label': `Selecionar conta ${params.row.cc}` }}
                />
            )
        },
        { field: 'cc', headerName: 'CCs', width: 150 },
        { field: 'valor', headerName: 'Valor', width: 150 },
        { field: 'vencimento', headerName: 'Vencimento', width: 150 },
        { field: 'status', headerName: 'Status', width: 150 }
    ], [selectedId]);

    const showResponseAlert = (response) => {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    };

    const fetchCCs = async () => {
        if (!documento) {
            return;
        }

        const body = JSON.stringify({ documento });
        const response = await fetchFlask('getBillssAgreement', body);

        if (response.bills) {
            setRows(response.bills.map((bill, index) => ({
                id: index,
                ...bill
            })));
            setSelectedId(null);
        } else {
            showResponseAlert({
                message: response.message || 'Erro ao buscar CCs',
                status: 'error'
            });
        }
    };

    async function submitRecebimento(e) {
        e.preventDefault();

        if (!canSubmit) {
            return;
        }

        setIsSubmit(true);
        const body = JSON.stringify({
            codigo_lancamento: selectedRow.codigo,
            valor_original: parseNumberPtBr(selectedRow.valor),
            valor_recebido: Number(valorRecebido),
            data_recebimento: formatIsoDate(dataRecebimento)
        });

        try {
            const response = await fetchFlask('lancarRecebimentoLimpaNome', body);
            showResponseAlert(response);
            if (response.status === 'success') {
                setDocumento('');
                setRows([]);
                setSelectedId(null);
                setValorRecebido('');
                setDataRecebimento(null);
            }
        } finally {
            setIsSubmit(false);
        }
    }

    const summaryItems = [
        { label: 'CPF/CNPJ', value: documento || 'Não informado', empty: !documento },
        { label: 'Conta selecionada', value: selectedRow?.cc || 'Aguardando seleção', empty: !selectedRow },
        {
            label: 'Valor original',
            value: selectedRow ? formatCurrency(parseNumberPtBr(selectedRow.valor)) : 'Aguardando seleção',
            empty: !selectedRow
        },
        { label: 'Vencimento', value: selectedRow?.vencimento || 'Aguardando seleção', empty: !selectedRow },
        {
            label: 'Valor recebido',
            value: valorValido ? formatCurrency(Number(valorRecebido)) : 'Não informado',
            empty: !valorValido
        },
        {
            label: 'Data de recebimento',
            value: formatDateLabelPtBr(dataRecebimento, 'Não definida'),
            empty: !dataRecebimento
        }
    ];

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Recebimento Limpa Nome</PageHeader>

            <form action='' method='POST' id='formRecebimentoLimpaNome' onSubmit={submitRecebimento} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Recebimento de conta em atraso</h2>
                        </Box>
                        <p>Localize as contas do cliente, selecione a conta recebida e informe o valor e a data do recebimento.</p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <FindInPageRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Consulta do cliente</strong>
                            </Box>
                        </Box>

                        <Box className='ui-lookup-grid'>
                            <TextField
                                required
                                label='CPF/CNPJ'
                                value={documento}
                                inputProps={{ maxLength: 18 }}
                                onChange={(e) => setDocumento(maskCpfCnpj(e.target.value))}
                            />
                            <Button
                                type='button'
                                variant='contained'
                                onClick={fetchCCs}
                                startIcon={<SearchRoundedIcon />}
                            >
                                Buscar CCs
                            </Button>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel agreement-workflow-bills-panel'>
                        <Box className='ui-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Conta recebida</strong>
                            </Box>
                        </Box>

                        <Box className='agreement-workflow-grid-shell'>
                            <DataGrid
                                rows={rows}
                                columns={billColumns}
                                onRowClick={(params) => setSelectedId(params.id)}
                                rowSelectionModel={selectedId === null ? [] : [selectedId]}
                                localeText={{ noRowsLabel: 'Sem Unidades' }}
                                hideFooter
                                autoHeight
                            />
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <PaymentsRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Recebimento</strong>
                            </Box>
                        </Box>

                        <Box className='agreement-workflow-form-grid'>
                            <TextField
                                required
                                label='Valor recebido'
                                type='number'
                                inputProps={{ step: 0.01, min: 0 }}
                                value={valorRecebido}
                                onChange={(e) => setValorRecebido(e.target.value)}
                            />
                            <DayDatePicker
                                id='dataRecebimento'
                                label='Data de recebimento'
                                required
                                value={dataRecebimento}
                                onChange={setDataRecebimento}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do recebimento</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        {summaryItems.map((item) => (
                            <Box key={item.label}>
                                <span>{item.label}</span>
                                <strong className={item.empty ? 'is-empty' : undefined}>{item.value}</strong>
                            </Box>
                        ))}
                    </Box>

                    <Box className='ui-attention'>
                        <WarningAmberRoundedIcon />
                        <p>O recebimento será lançado somente para a conta selecionada, com o valor e a data informados.</p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/4 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton disabled={!canSubmit} isSubmit={isSubmit} startIcon={<PaidRoundedIcon />}>
                        Lançar Recebimento
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default RecebimentoLimpaNome;
